import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { notificationPanel, spring } from "@/lib/motion";
import NotificationItem from "./NotificationItem";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

// ─── Types ───────────────────────────────────────────────────────────────────

interface NotificationPanelProps {
  /** Called when the panel should close (e.g. after clicking a notification or "View All"). */
  onClose?: () => void;
  /** Additional CSS classes for the container. */
  className?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of notifications to display in the popover panel. */
const MAX_PANEL_ITEMS = 5;

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Notification panel popover component anchored below the bell icon.
 *
 * Features:
 * - Header: "Notifications" title + "Mark all as read" button (if any unread)
 * - Notification list: max 5 recent items using NotificationItem components
 *   - Unread items have a blue dot indicator and slightly different background
 *   - Click marks as read and navigates to the notification link
 * - Footer: "View All Notifications" link → Notification Center page
 * - Empty state: "No notifications" with bell illustration
 * - Animated entrance: items stagger in with spring physics
 * - Smooth exit on close
 * - Max height with scroll (340px list area)
 *
 * Consumes the NotificationContext for state and actions.
 */
export default function NotificationPanel({
  onClose,
  className,
}: NotificationPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } =
    useNotificationContext();
  const prefersReducedMotion = usePrefersReducedMotion();

  // ── Derived ────────────────────────────────────────────────────────────────

  /** Only show the first N notifications in the panel. */
  const panelNotifications = notifications.slice(0, MAX_PANEL_ITEMS);
  const hasUnread = unreadCount > 0;
  const isEmpty = notifications.length === 0 && !isLoading;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
    } catch {
      // Best-effort — toast would be shown by the hook/context if needed
    }
  };

  const handleNotificationRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
    } catch {
      // Best-effort
    }
    // Close the panel after clicking a notification (it navigates to the link)
    onClose?.();
  };

  const handleViewAll = () => {
    navigate(`/${lang}/notifications`);
    onClose?.();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── Panel animation variants (from motion.ts presets) ──────────────────────

  const panelVariants = prefersReducedMotion
    ? {
        hidden: { opacity: 1, scale: 1, y: 0 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0 } },
        exit: { opacity: 0, scale: 1, y: 0, transition: { duration: 0 } },
      }
    : notificationPanel;

  return (
    <motion.div
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{ transformOrigin: "top right" }}
      className={cn("flex flex-col overflow-hidden rounded-lg", className)}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            {t("notifications.title", "Notifications")}
          </h3>
          {hasUnread && (
            <motion.span
              initial={prefersReducedMotion ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      type: "spring",
                      stiffness: spring.stiff.stiffness,
                      damping: spring.stiff.damping,
                    }
              }
              className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-bold text-primary"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </motion.span>
          )}
        </div>

        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            {t("notifications.markAllRead", "Mark all read")}
          </Button>
        )}
      </div>

      {/* ── Notification List ──────────────────────────────────────────── */}
      <ScrollArea className="max-h-[340px]">
        <div className="py-1">
          {/* Loading state */}
          {isLoading && notifications.length === 0 && (
            <div className="flex items-center justify-center px-4 py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {isEmpty && (
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                prefersReducedMotion ? { duration: 0 } : { delay: 0.05 }
              }
              className="flex flex-col items-center justify-center px-4 py-10 text-center"
            >
              <motion.div
                initial={
                  prefersReducedMotion ? false : { scale: 0.8, opacity: 0 }
                }
                animate={{ scale: 1, opacity: 1 }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : {
                        type: "spring",
                        stiffness: spring.smooth.stiffness,
                        damping: spring.smooth.damping,
                        delay: 0.1,
                      }
                }
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Bell className="h-6 w-6 text-muted-foreground/50" />
                </div>
              </motion.div>

              <p className="text-sm font-medium text-foreground/80">
                {t("notifications.empty", "No notifications")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t(
                  "notifications.emptyDescription",
                  "You're all caught up! New notifications will appear here.",
                )}
              </p>
            </motion.div>
          )}

          {/* Notification items */}
          <AnimatePresence mode="popLayout">
            {panelNotifications.map((notification, index) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRead={handleNotificationRead}
                variant="compact"
                index={index}
              />
            ))}
          </AnimatePresence>
        </div>
      </ScrollArea>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      {!isEmpty && (
        <>
          <Separator />
          <div className="px-4 py-2.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAll}
              className="w-full justify-center text-xs font-medium text-primary hover:text-primary/80"
            >
              {t("notifications.viewAll", "View All Notifications")}
              {notifications.length > MAX_PANEL_ITEMS && (
                <span className="ml-1 text-muted-foreground">
                  ({notifications.length}+)
                </span>
              )}
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
}
